import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Building2,
  Plus,
  MoreVertical,
  Users,
  FolderOpen,
  Loader2,
  Search,
  Ban,
  CheckCircle,
  Edit,
  Eye
} from 'lucide-react';
import { apiErrorMessage } from '../../lib/apiError';
import { toast } from 'sonner';
import api from '../../lib/api';

const AdminOrganizationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [platformStats, setPlatformStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);

  // Form states
  const [createForm, setCreateForm] = useState({ name: '', description: '', billing_email: '', plan: 'free' });
  const [editForm, setEditForm] = useState({ name: '', description: '', billing_email: '', plan: 'free' });
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [orgsRes, statsRes] = await Promise.all([
        api.get('/organizations'),
        api.get('/organizations/platform/stats')
      ]);
      setOrganizations(orgsRes.data);
      setPlatformStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredOrgs = organizations.filter((org) => {
    const matchesSearch = !searchQuery ||
      org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (org.billing_email || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlan = planFilter === 'all' || org.plan === planFilter;
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && org.is_active) ||
      (statusFilter === 'inactive' && !org.is_active);
    return matchesSearch && matchesPlan && matchesStatus;
  });

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/organizations', createForm);
      toast.success('Organization created');
      setShowCreateDialog(false);
      setCreateForm({ name: '', description: '', billing_email: '', plan: 'free' });
      await fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to create organization'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOrg = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    setSubmitting(true);
    try {
      await api.patch(`/organizations/${selectedOrg.id}`, editForm);
      toast.success('Organization updated');
      setShowEditDialog(false);
      setSelectedOrg(null);
      await fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to update organization'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedOrg) return;
    setSubmitting(true);
    try {
      if (selectedOrg.is_active) {
        await api.delete(`/organizations/${selectedOrg.id}`);
        toast.success('Organization deactivated');
      } else {
        await api.patch(`/organizations/${selectedOrg.id}`, { is_active: true });
        toast.success('Organization reactivated');
      }
      setShowDeactivateDialog(false);
      setSelectedOrg(null);
      await fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to update organization'));
    } finally {
      setSubmitting(false);
    }
  };

  const openUsersDialog = async (org) => {
    setSelectedOrg(org);
    setShowUsersDialog(true);
    setLoadingUsers(true);
    try {
      const res = await api.get(`/organizations/${org.id}/users`);
      setOrgUsers(res.data);
    } catch {
      setOrgUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const formatRole = (role) => {
    return role?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '';
  };

  const getPlanBadge = (plan) => {
    switch (plan) {
      case 'enterprise':
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Enterprise</Badge>;
      case 'pro':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Pro</Badge>;
      default:
        return <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">Free</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900">Organizations</h1>
          <Badge variant="outline" className="text-slate-500">
            {organizations.length} total
          </Badge>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Organization
        </Button>
      </div>

      {/* Platform Stats */}
      {platformStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Organizations</CardDescription>
              <CardTitle className="text-2xl">{platformStats.total_organizations}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500">{platformStats.active_organizations} active</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Users</CardDescription>
              <CardTitle className="text-2xl">{platformStats.total_users}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500">{platformStats.recent_signups} joined this week</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Projects</CardDescription>
              <CardTitle className="text-2xl">{platformStats.total_projects}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500">{platformStats.total_schedules} schedules</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Activities</CardDescription>
              <CardTitle className="text-2xl">{platformStats.total_activities}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500">Across all schedules</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
        <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search organizations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-full sm:w-[140px] h-9">
            <SelectValue placeholder="All Plans" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[140px] h-9">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Organizations Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Users</TableHead>
                <TableHead className="text-center">Projects</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrgs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-slate-500">
                    No organizations found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrgs.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{org.name}</p>
                        {org.billing_email && (
                          <p className="text-xs text-slate-500">{org.billing_email}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getPlanBadge(org.plan)}</TableCell>
                    <TableCell>
                      {org.is_active ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Users className="h-3 w-3 text-slate-400" />
                        <span>{org.user_count}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <FolderOpen className="h-3 w-3 text-slate-400" />
                        <span>{org.project_count}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500">
                        {new Date(org.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openUsersDialog(org)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Users
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setSelectedOrg(org);
                            setEditForm({
                              name: org.name,
                              description: org.description || '',
                              billing_email: org.billing_email || '',
                              plan: org.plan
                            });
                            setShowEditDialog(true);
                          }}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {org.is_active ? (
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setSelectedOrg(org);
                                setShowDeactivateDialog(true);
                              }}
                            >
                              <Ban className="h-4 w-4 mr-2" />
                              Deactivate
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              className="text-green-600"
                              onClick={() => {
                                setSelectedOrg(org);
                                setShowDeactivateDialog(true);
                              }}
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Reactivate
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>
              Create a new organization on the platform.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateOrg} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name</Label>
              <Input
                id="org-name"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Acme Construction Inc."
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-desc">Description</Label>
              <Input
                id="org-desc"
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                placeholder="General contractor specializing in..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-email">Billing Email</Label>
              <Input
                id="org-email"
                type="email"
                value={createForm.billing_email}
                onChange={(e) => setCreateForm({ ...createForm, billing_email: e.target.value })}
                placeholder="billing@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={createForm.plan} onValueChange={(v) => setCreateForm({ ...createForm, plan: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogDescription>
              Update details for {selectedOrg?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditOrg} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Organization Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-desc">Description</Label>
              <Input
                id="edit-desc"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Billing Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.billing_email}
                onChange={(e) => setEditForm({ ...editForm, billing_email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={editForm.plan} onValueChange={(v) => setEditForm({ ...editForm, plan: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Users Dialog */}
      <Dialog open={showUsersDialog} onOpenChange={setShowUsersDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Users in {selectedOrg?.name}</DialogTitle>
            <DialogDescription>
              {selectedOrg?.user_count} members in this organization
            </DialogDescription>
          </DialogHeader>
          {loadingUsers ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : orgUsers.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No users in this organization</p>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgUsers.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{formatRole(u.role)}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deactivate/Reactivate Dialog */}
      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${selectedOrg?.is_active ? 'text-red-600' : 'text-green-600'}`}>
              {selectedOrg?.is_active ? <Ban className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
              {selectedOrg?.is_active ? 'Deactivate' : 'Reactivate'} Organization
            </DialogTitle>
            <DialogDescription>
              {selectedOrg?.is_active
                ? `Are you sure you want to deactivate ${selectedOrg?.name}? Users will still exist but the organization will be marked as inactive.`
                : `Reactivate ${selectedOrg?.name}? The organization will become active again.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeactivateDialog(false)}>Cancel</Button>
            <Button
              variant={selectedOrg?.is_active ? "destructive" : "default"}
              onClick={handleToggleStatus}
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {selectedOrg?.is_active ? 'Deactivate' : 'Reactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrganizationsPage;
